import { type CSSProperties } from 'react';

const HEX_RADIX = 16;
const HEX_SHORT_LENGTH = 3;
const HEX_FULL_LENGTH = 6;
const HEX_RED_END = 2;
const HEX_GREEN_START = 2;
const HEX_GREEN_END = 4;
const HEX_BLUE_START = 4;
const HEX_BLUE_END = 6;
const LUMA_R = 299;
const LUMA_G = 587;
const LUMA_B = 114;
const LUMA_DIVISOR = 1000;
const YIQ_LIGHT_THRESHOLD = 128;

/**
 * Calculates relative luminance of a hex color (with or without #) using the YIQ formula.
 * Returns null when the value is not a parseable 3- or 6-digit hex color.
 */
function getRelativeLuminance(hex: string): number | null {
  const cleanHex = hex.replace('#', '');

  const fullHex =
    cleanHex.length === HEX_SHORT_LENGTH
      ? cleanHex[0] + cleanHex[0] + cleanHex[1] + cleanHex[1] + cleanHex[2] + cleanHex[2]
      : cleanHex;

  if (fullHex.length !== HEX_FULL_LENGTH) {
    return null;
  }

  const r = Number.parseInt(fullHex.substring(0, HEX_RED_END), HEX_RADIX);
  const g = Number.parseInt(fullHex.substring(HEX_GREEN_START, HEX_GREEN_END), HEX_RADIX);
  const b = Number.parseInt(fullHex.substring(HEX_BLUE_START, HEX_BLUE_END), HEX_RADIX);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return null;
  }

  return (r * LUMA_R + g * LUMA_G + b * LUMA_B) / LUMA_DIVISOR;
}

/**
 * Determines the appropriate contrast color for text/icons based on background color.
 *
 * @param hex - The hex color value (with or without #)
 * @returns 'rgba(0, 0, 0, 0.75)' for light backgrounds, 'rgba(255, 255, 255, 0.75)' for dark backgrounds
 */
export const getContrastColor = (hex: string): string => {
  const yiq = getRelativeLuminance(hex);

  if (yiq === null) {
    return 'rgba(0, 0, 0, 0.75)';
  }

  return yiq >= YIQ_LIGHT_THRESHOLD ? 'rgba(0, 0, 0, 0.75)' : 'rgba(255, 255, 255, 0.75)';
};

/** Full-opacity text/icon color for filled buttons on a custom background. */
export const getSolidContrastColor = (hex: string): string => {
  const yiq = getRelativeLuminance(hex);

  if (yiq === null) {
    return '#111827';
  }

  return yiq >= YIQ_LIGHT_THRESHOLD ? '#111827' : '#ffffff';
};

/** Inline style for brand-colored buttons — the backend stores no foreground colors, so derive one. */
export const getBrandedButtonStyle = (buttonColor: string): CSSProperties => ({
  backgroundColor: buttonColor,
  borderColor: buttonColor,
  color: getSolidContrastColor(buttonColor),
});
